import { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack,
    Typography, Alert, Divider,
} from '@mui/material';
import { adminFetch, normalizeEmail } from '../../utils/authUtils';

const TOGGLEABLE = ['Premium', 'Comp'];

export default function UserDetailDialog({ user, onClose, onChanged }) {
    const [groups, setGroups] = useState(user.groups);
    const [attrs, setAttrs] = useState({
        email: user.email || '',
        name: user.name || '',
        phone_number: user.phone_number || '',
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [savedNote, setSavedNote] = useState(null);

    const toggleGroup = async (group) => {
        setBusy(true); setError(null); setSavedNote(null);
        try {
            const action = groups.includes(group) ? 'remove' : 'add';
            const data = await adminFetch('/admin/users/groups', {
                username: user.username, group, action,
            });
            setGroups(data.groups);
            setSavedNote(`${action === 'add' ? 'Added to' : 'Removed from'} ${group}.`);
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const saveAttributes = async () => {
        setBusy(true); setError(null); setSavedNote(null);
        try {
            // Canonicalize email before diffing + sending so case-only changes
            // are not treated as edits and writes go in lowercased.
            const canonical = {
                ...attrs,
                email: normalizeEmail(attrs.email),
            };
            const changed = Object.fromEntries(
                Object.entries(canonical).filter(([k, v]) => v !== (user[k] || '')),
            );
            if (Object.keys(changed).length === 0) {
                setSavedNote('No changes.');
                return;
            }
            await adminFetch('/admin/users/attributes', {
                username: user.username, attributes: changed,
            });
            setSavedNote('Saved.');
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open maxWidth="sm" fullWidth onClose={onClose}>
            <DialogTitle>{user.email || user.username}</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {savedNote && <Alert severity="success" sx={{ mb: 2 }}>{savedNote}</Alert>}

                <Typography variant="overline" sx={{ color: 'text.muted' }}>Groups</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
                    {TOGGLEABLE.map(g => (
                        <Button
                            key={g}
                            variant={groups.includes(g) ? 'contained' : 'outlined'}
                            disabled={busy}
                            onClick={() => toggleGroup(g)}
                            size="small"
                        >
                            {groups.includes(g) ? `Remove ${g}` : `Add ${g}`}
                        </Button>
                    ))}
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.muted', display: 'block', mb: 2 }}>
                    Current: {groups.join(', ') || '(none)'}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="overline" sx={{ color: 'text.muted' }}>Attributes</Typography>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField label="Email" value={attrs.email}
                        onChange={e => setAttrs({ ...attrs, email: e.target.value })} size="small" fullWidth />
                    <TextField label="Name" value={attrs.name}
                        onChange={e => setAttrs({ ...attrs, name: e.target.value })} size="small" fullWidth />
                    <TextField label="Phone" value={attrs.phone_number}
                        onChange={e => setAttrs({ ...attrs, phone_number: e.target.value })} size="small" fullWidth
                        helperText="Include country code, e.g. +15551234567" />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button onClick={onChanged} variant="text">Refresh list</Button>
                <Button onClick={saveAttributes} disabled={busy} variant="contained">
                    Save attributes
                </Button>
            </DialogActions>
        </Dialog>
    );
}
